import {Component, inject, Input, OnInit, signal} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {animate, transition, trigger} from '@angular/animations';
import {ButtonInterface} from '../../../../common/components/button/button.model';
import {ModalCloseFeedBack} from '../../../../common/components/modal/modal.model';
import {Observable} from 'rxjs';
import {Field} from "../../../../common/record/field.model";
import {Record} from "../../../../common/record/record.model";
import {FormControl} from "@angular/forms";
import {ExportExcelModalService} from "./export-excel-modal.service";
import {AsyncActionInput} from "../../../../services/process/processes/async-action/async-action";
import {map, tap} from "rxjs/operators";
import {isTrue} from "../../../../common/utils/value-utils";
import {Router} from "@angular/router";
import {ExportExcelModalButtons, ExportExcelModalConfig, ExportExcelRedirect} from "./export-excel-modal.models";

@Component({
    selector: 'scrm-export-excel-modal',
    templateUrl: './export-excel-modal.component.html',

    animations: [
        trigger('modalFade', [
            transition('void <=> *', [
                animate('800ms')
            ]),
        ]),
    ],
    providers: [ExportExcelModalService]
})
export class ExportExcelModalComponent implements OnInit {

    service: ExportExcelModalService = inject(ExportExcelModalService);

    @Input() config: ExportExcelModalConfig;
    @Input() asyncActionInput: AsyncActionInput;

    loading = true;

    module: string;
    fields: Field[] = []; // tick and trick
    fields$: Observable<Field[]>
    record: Record;
    selectExportTypeField: Field;

    buttons: ExportExcelModalButtons[];
    closeButton: ButtonInterface;

    constructor(
        public activeModal: NgbActiveModal,
        private router: Router
    ) {

    }

    private setupConfig() {
        this.record = this.service.getRecord(this.asyncActionInput.module);
    }

    ngOnInit(): void {
        this.setupConfig();
        this.setupModalCloseButton();
        this.loadFields();
        this.setupSelectExportType();
        this.setupButtons();
    }

    private loadFields() {
        this.fields$ = this.service.loadData(this.asyncActionInput).pipe(
            map((fields: Field[]) => {
                fields.forEach((field: Field) => {
                    field.value = field.value;
                    field.formControl = new FormControl(field['name']);
                    field.required = signal(false);
                    field.loading = signal(false);
                    field.display = signal('default');
                    this.fields.push(field);
                });
                return fields;
            }),
            tap(() => {
                this.loading = false;
            }));
    }

    private setupModalCloseButton(): void {
        this.closeButton = {
            klass: ['btn', 'btn-outline-light', 'btn-sm'],
            onClick: (): void => {
                this.activeModal.close({
                    type: 'close-button'
                } as ModalCloseFeedBack);
            }
        } as ButtonInterface;
    }

    private setupSelectExportType(): void {
        this.selectExportTypeField = this.config.export_type_select as Field;
        this.selectExportTypeField.required = signal(false);
        this.selectExportTypeField.loading = signal(false);
        this.selectExportTypeField.display = signal('default');
        this.selectExportTypeField.formControl = new FormControl(this.config.export_type_select.value);
    }

    private setupButtons(): void {
        this.buttons = this.config.buttons.map((buttonConfig: ExportExcelModalButtons) => {
            if (buttonConfig.type === 'button') {
                buttonConfig.onClick = this.buttonClick.bind(this, buttonConfig.onclickAction);
            } else if (buttonConfig.type === 'submit') {
                buttonConfig.onClick = this.submitClick.bind(this);
            }
            return buttonConfig;
        });
    }


    submitClick(): void {
        const submitFields = {};
        this.fields.forEach((field) => {
            if (isTrue(field.value)) {
                submitFields[field.name] = field.label;
            }
        });

        if(Object.keys(submitFields).length === 0){
            return;
        }

        this.asyncActionInput.payload = {
            'fieldsList': submitFields,
            'export_type': this.selectExportTypeField.formControl.value,
        };

        this.service.runExport(this.asyncActionInput).subscribe((redirect: ExportExcelRedirect) => {
            this.activeModal.close();
            if (redirect.blank) {
                window.open(`/#/${redirect['module']}/record/${redirect['id']}`, '_blank');
                return;
            }
            this.router.navigate([redirect['module'], 'record', redirect['id']]).then();
        });

    }

    buttonClick(actionName: string): void {
        if (actionName === 'check-all') {
            this.toggleFields('true');
        } else if (actionName === 'uncheck-all') {
            this.toggleFields('false');
        }
    }

    toggleFields(value: string) {
        Object.keys(this.fields).forEach((key) => {
            this.fields[key].value = value;
        });
    }
}
